import Vue from 'vue'

import ApiService from '@/core/services/api.service'
import JwtService from '@/core/services/jwt.service'

import { LOGOUT } from './auth.module.js'
import router from '@/router'

import { eventBus } from '@/main'
import { groupByCountry } from '@/core/utils.js'

// action types
export const GET_RESELLERS = 'getResellers'
export const GET_RESELLER = 'getReseller'
export const CHANGE_RESELLER_TARIFF = 'manageReseller'
export const SWITCH_RESELLER = 'switchReseller'
export const GENERATE_USER_RESELLER = 'generateUserReseller'
export const SET_RESELLER_ADDON = 'setResellerAddon'
export const DELETE_RESELLER = 'deleteReseller'
export const GET_RESELLER_SERVERS = 'getResellerServers'

// mutation types
export const SAVE_RESELLERS = 'saveResellers'
export const SAVE_RESELLER = 'saveReseller'
export const SET_FAILED_GET_RESELLER_STATUS = 'setFailedGetResellerStatus'
export const SET_RESELLERS_LIMIT = 'setResellersLimit'
export const SET_RESELLERS_PAGE = 'setResellersPage'
export const SET_RESELLERS_GET_PARAM = 'setResellersGetParam'
export const SET_RESELLERS_GET_PARAMS = 'setResellersGetParams'
export const CLEAR_RESELLERS_GET_PARAMS = 'clearResellersGetParams'
export const CLEAR_RESELLERS = 'clearResellers'
export const SELECT_RESELLER = 'selectReseller'
export const UNSELECT_RESELLER = 'unselectReseller'
export const SAVE_GENERATED_USER_DATA = 'saveGeneratedUserData'
export const CLEAR_GENERATED_USER_DATA = 'clearGeneratedUserData'
export const CLEAR_GENERATE_USER_PAYLOAD = 'clearGenerateUserPayload'
export const SAVE_GENERATE_USER_ERRORS = 'saveManageResellerErrors'
export const CLEAR_GENERATE_USER_ERRORS = 'clearManageResellerErrors'
export const SAVE_RESELLER_SERVERS = 'saveResellerServers'
export const CLEAR_RESELLER_SERVERS = 'clearResellerServers'

const SET_RESPONSE_STATE = 'setResponseState'
export const TOAST_RESELLERS = 'showToast/ResellersList'
export const TOAST_INTEGRATIONS = 'showToast/ResellerIntegrations'
export const DEFAULT_SELECTED_RESELLER = {
  id: undefined, // extrernal ID from billing system
  _id: undefined, // backend database ID
  created: new Date().toISOString(),
  users: {
    total: 0,
    additional: 0,
    limit: 0
  },
  devices: {
    total: 0,
    additional: 0,
    limit: 0
  },
  tariff: {
    _id: null,
    name: '',
    devices: 0,
    users: 0,
  },
  api_key: '',
  api_ip: [],
  integrations: [],
  webhook_url: null,
  active: false,
}
export const DEFAULT_RESELLERS_GET_PARAMS = {
  limit: 10,
  page: 1,
  sort: 'created',
  ord: 'desc',
  tariff: null,
  status: null,
}
export const DEFAULT_RESELLERS_ROUTER_PARAMS = {
  limit: '10',
  page: '1',
  sort: 'created',
  ord: 'desc'
}
export const DEFAULT_GENERATE_USER_PAYLOAD = {
  devices: null,
  expiry: getDefaultExpiry(),
  days: 30,
}
export const DEFAULT_GENERATED_USER_DATA = {
  _id: undefined,
  code: '',
  token: '',
  created: new Date().toISOString(),
  expiry: new Date().toISOString(),
  devices: 0,
  status: 'on'
}

// returns reseller based on _id
function findReseller(state, id) {
  return state.resellersList.filter(item => item._id === id)[0]
}

// clean each param that is null or undefined
function sanitizeGetRequestPayload(state) {
  const getParams = { ...state.resellersGetParams }
  Object.keys(getParams).forEach(key => {
    if (!getParams[key]) {
      delete getParams[key]
    }
  })
  return getParams
}

// function deletes .days param used only on front-end
// back-end requires only .expiry param in ISO format
function sanitizeGenerateUserPayload(state) {
  const generateUserPayload = { ...state.generateUserPayload }
  delete generateUserPayload.days
  return generateUserPayload
}

// returns a date 30 days from today
function getDefaultExpiry() {
  const today = new Date()
  const pastMonth = new Date(today)
  pastMonth.setDate(pastMonth.getDate() + 30)
  return pastMonth.toISOString().split('T')[0]
}

const state = {
  resellersGetParams: { ...DEFAULT_RESELLERS_GET_PARAMS },
  resellersList: [],
  totalResellers: 0,
  totalResellerPages: 0,
  selectedReseller: { ...DEFAULT_SELECTED_RESELLER },
  selectedResellerId: null,
  generateUserPayload: { ...DEFAULT_GENERATE_USER_PAYLOAD },
  generateUserErrors: [],
  generatedUserData: { ...DEFAULT_GENERATED_USER_DATA },
  addonErrors: [],
  resellerServers: {},
  failedGetResellerStatus: undefined,

  responses: {
    resellersList: false,
    getReseller: false,
    switchReseller: false,
    changeResellerTariff: false,
    generateUserReseller: false,
    setResellerAddon: false,
    deleteReseller: false,
    getResellerServers: false,
  },
}

const getters = {
  resellersList(state) {
    return state.resellersList
  },
  resellersListLength(state) {
    return state.resellersList.length
  },
  resellersEmpty(state) {
    return state.resellersList.length == 0
  },
  // At first I made getter 'resellersGetParams' which returns whole object,
  // but it didn't worked out very well.
  // We need to separate params from state.resellersGetParams
  // because Vue sometimes triggers watch() event twice when on of them changes.
  // This happenes because <b-pagination> and <b-form-select> are synched.
  // In that case Axios could make two GET requests at once which is bad.
  resellersLimit(state) {
    return state.resellersGetParams.limit
  },
  resellersPage(state) {
    return state.resellersGetParams.page
  },
  resellersGetParams(state) {
    return state.resellersGetParams
  },
  totalResellers(state) {
    return state.totalResellers
  },
  totalResellerPages(state) {
    return state.totalResellerPages
  },
  selectedReseller(state) {
    return state.selectedReseller
  },
  selectedResellerId(state) {
    return state.selectedResellerId
  },
  selectedResellerExist(state) {
    return state.resellersList.some(reseller => reseller._id == state.selectedResellerId)
  },
  generateUserPayload(state) {
    return state.generateUserPayload
  },
  generateUserErrors(state) {
    return state.generateUserErrors
  },
  generateUserError: state => field => {
    return state.generateUserErrors.filter(err => err.field == field)
  },
  resellerStatusBadge: () => reseller => {
    const badgeOptions = {
      variant: null,
      icon: null,
      text: null,
    }

    badgeOptions.variant = reseller.active ? 'success' : 'danger'
    badgeOptions.icon = reseller.active ? 'check' : 'times'
    badgeOptions.text = reseller.active ? 'Active' : 'Disabled'

    return badgeOptions
  },
  resellerCapacity: () => (item, param) => {
    const statusMap = {
      normal: { status: 'normal', className: 'text-default' },
      warning: { status: 'warning', className: 'text-warning' },
      danger: { status: 'danger', className: 'text-danger' },
    }

    if (item) {
      const percentage = (item[param].total / item[param].limit) * 100

      if (percentage < 70) {
        return statusMap.normal
      } else if (percentage >= 70 && percentage < 90) {
        return statusMap.warning
      } else if (percentage >= 90) {
        return statusMap.danger
      } else {
        return statusMap.normal
      }
    }
  },
  generatedUserStatusBadge: () => user => {
    const badgeOptions = {
      variant: null,
      icon: null,
      text: null,
    }

    badgeOptions.variant = user.status == 'on' ? 'success' : 'danger'
    badgeOptions.icon = user.status == 'on' ? 'check' : 'times'
    badgeOptions.text = user.status == 'on' ? 'Active' : 'Disabled'

    return badgeOptions
  },
  generatedUserData(state) {
    return state.generatedUserData
  },
  userIsGenerarated(state) {
    return state.generatedUserData._id !== undefined
  },
  generateUserModalTitle(state, getters) {
    return getters.userIsGenerarated ? 'New user generated! ✓' : 'Generate new user'
  },
  addonErrors(state) {
    return state.addonErrors
  },
  addonError: state => field => {
    return state.addonErrors.filter(err => err.field == field)
  },
  resellerServers(state) {
    return state.resellerServers
  },
  resellerServersEmpty(state) {
    return Object.keys(state.resellerServers).length === 0
  },
  resellerHasIntegrations: state => id => {
    const target = findReseller(state, id)
    if (target) {
      return target.integrations.length > 0
    }
  },
  resellerIntegrations: state => id => {
    const target = findReseller(state, id)
    if (target) {
      return target.integrations
    }
  },
  failedGetResellerStatus(state) {
    return state.failedGetResellerStatus
  },
  resellersResponses: state => type => {
    return state.responses[type]
  }
}

const actions = {
  [GET_RESELLERS](context) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'resellersList', status: true })
        eventBus.$emit('startProgress')
        ApiService.setHeader()
        const payload = sanitizeGetRequestPayload(context.state)
        ApiService.get(process.env.VUE_APP_RESELLERS, payload)
          .then(({ data }) => {
            context.commit(SAVE_RESELLERS, data)
            resolve(data)
          })
          .catch(err => {
            eventBus.$emit(TOAST_RESELLERS, {
              message: err.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'resellersList', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [GET_RESELLER](context, { id }) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'getReseller', status: true })
        eventBus.$emit('startProgress')
        ApiService.setHeader()
        ApiService.get(`${process.env.VUE_APP_RESELLER}/${id}`)
          .then(({ data }) => {
            context.commit(SET_FAILED_GET_RESELLER_STATUS, false)
            context.commit(SAVE_RESELLER, data)
            resolve(data)
          })
          .catch(err => {
            context.commit(SET_FAILED_GET_RESELLER_STATUS, true)
            eventBus.$emit(TOAST_INTEGRATIONS, {
              message: err.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'getReseller', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [CHANGE_RESELLER_TARIFF](context, { reseller_id, tariff_id }) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'changeResellerTariff', status: true })
        eventBus.$emit('startProgress')
        ApiService.patch(
          `${process.env.VUE_APP_RESELLER}/${reseller_id}`,
          { tariff: tariff_id }
          )
          .then(({ data }) => {
            context.dispatch(GET_RESELLERS)
            eventBus.$emit(TOAST_RESELLERS, {
              message: data.message,
              variant: 'success'
            })
            resolve(data)
          })
          .catch(err  => {
            const error = err.response.data
            eventBus.$emit(TOAST_RESELLERS, {
              message: error.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'changeResellerTariff', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [SWITCH_RESELLER](context, { id }) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'switchReseller', status: true })
        eventBus.$emit('startProgress')
        const payload = state.selectedReseller.active ? 'disable' : 'enable'
        ApiService.put(`${process.env.VUE_APP_RESELLER}/${id}/${payload}`)
          .then(({ data }) => {
            context.dispatch(GET_RESELLER, { id })
            eventBus.$emit(TOAST_RESELLERS, {
              message: data.message,
              variant: 'info'
            })
            resolve(data)
          })
          .catch(err  => {
            const error = err.response.data
            eventBus.$emit(TOAST_RESELLERS, {
              message: error.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'switchReseller', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [GENERATE_USER_RESELLER](context, { reseller_id }) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'generateUserReseller', status: true })
        eventBus.$emit('startProgress')
        const payload = sanitizeGenerateUserPayload(context.state)
        ApiService.post(`${process.env.VUE_APP_RESELLER}/${reseller_id}/user`, payload)
          .then(({ data }) => {
            context.commit(SAVE_GENERATED_USER_DATA, data)
            eventBus.$emit(TOAST_RESELLERS, {
              message: data.message,
              variant: 'info'
            })
            resolve(data)
          })
          .catch(err  => {
            const error = err.response.data
            context.commit(SAVE_GENERATE_USER_ERRORS, error.errors)
            if (error) {
              eventBus.$emit(TOAST_RESELLERS, {
                message: error.message,
                variant: 'danger'
              })
            }
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'generateUserReseller', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [SET_RESELLER_ADDON](context, { id, addon }) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'setResellerAddon', status: true })
        eventBus.$emit('startProgress')
        ApiService.patch(`${process.env.VUE_APP_RESELLER}/${id}`, addon)
          .then(({ data }) => {
            eventBus.$emit(TOAST_RESELLERS, {
              message: data.message,
              variant: 'info'
            })
            resolve(data)
          })
          .catch(err  => {
            const error = err.response.data
            eventBus.$emit(TOAST_RESELLERS, {
              message: error.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'setResellerAddon', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [DELETE_RESELLER](context) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'deleteReseller', status: true })
        eventBus.$emit('startProgress')
        ApiService.delete(`${process.env.VUE_APP_RESELLER}/${state.selectedReseller.id}`)
          .then(({ data }) => {
            context.dispatch(GET_RESELLERS)
            eventBus.$emit(TOAST_RESELLERS, {
              message: data.message,
              variant: 'success'
            })
            resolve(data)
          })
          .catch(err => {
            const errors = err.response.data.errors
            for (let e = 0; e < errors.length; e++) {
              eventBus.$emit(TOAST_RESELLERS, {
                message: errors[e].message,
                variant: 'danger'
              })
            }
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'deleteReseller', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [GET_RESELLER_SERVERS](context, { reseller_id }) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'getResellerServers', status: true })
        eventBus.$emit('startProgress')
        ApiService.setHeader()
        ApiService.get(`${process.env.VUE_APP_RESELLER}/${reseller_id}/servers`)
          .then(({ data }) => {
            context.commit(SAVE_RESELLER_SERVERS, data)
            resolve(data)
          })
          .catch(err => {
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'getResellerServers', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
}

const mutations = {
  [SAVE_RESELLERS](state, resellers) {
    state.resellersList = resellers.list
    state.totalResellers = resellers.total_elements
    state.totalResellerPages = resellers.total_pages
  },
  [SAVE_RESELLER](state, reseller) {
    const target = findReseller(state, reseller._id)
    if (target) {
      Object.keys(reseller).forEach(key => {
        Vue.set(target, key, reseller[key])
      })
    }
  },
  [SET_FAILED_GET_RESELLER_STATUS](state, status) {
    state.failedGetResellerStatus = status
  },
  [CLEAR_RESELLERS](state) {
    state.resellersList = []
  },
  [SET_RESELLERS_LIMIT](state, limit) {
    state.resellersGetParams.limit = limit
  },
  [SET_RESELLERS_PAGE](state, page) {
    state.resellersGetParams.page = page
  },
  [SET_RESELLERS_GET_PARAM](state, { value, param }) {
    const origType = typeof state.resellersGetParams[param]
    
    if (origType !== 'undefined' && origType !== typeof value) {
      switch (origType) {
        case 'number':
          value = Number(value)
          break
        case 'string':
          value = String(value)
          break
      }
    }

    if (value === 'null') {
      value = null
    }

    state.resellersGetParams[param] = value
  },
  [SET_RESELLERS_GET_PARAMS](state, params) {
    const keys = Object.keys(params)
  
    for (const key of keys) {
      const origType = typeof state.resellersGetParams[key]
      const newValue = params[key]
  
      if (typeof newValue !== origType) {
        if (state.resellersGetParams[key] !== null) {
          switch (origType) {
            case 'number':
              Vue.set(state.resellersGetParams, key, Number(newValue))
              break
            case 'string':
              Vue.set(state.resellersGetParams, key, String(newValue))
              break
          }
        } else {
          Vue.set(state.resellersGetParams, key, newValue)
        }
      } else {
        Vue.set(state.resellersGetParams, key, newValue)
      }
    }
  },
  [CLEAR_RESELLERS_GET_PARAMS](state) {
    state.resellersGetParams = { ...DEFAULT_RESELLERS_GET_PARAMS }
  },
  [SELECT_RESELLER](state, reseller) {
    state.selectedReseller = reseller
    state.selectedResellerId = reseller._id
  },
  [UNSELECT_RESELLER](state) {
    state.selectedReseller = { ...DEFAULT_SELECTED_RESELLER }
    state.selectedResellerId = null
  },
  [SAVE_GENERATE_USER_ERRORS](state, errors) {
    state.generateUserErrors = errors
  },
  [CLEAR_GENERATE_USER_ERRORS](state) {
    state.generateUserErrors = []
  },
  [SAVE_GENERATED_USER_DATA](state, data) {
    if (data && Object.keys(data).length > 0) {
      Object.keys(data).forEach(key => {
        Vue.set(state.generatedUserData, key, data[key])
      })
    }
  },
  [CLEAR_GENERATED_USER_DATA](state) {
    state.generatedUserData = { ...DEFAULT_GENERATED_USER_DATA }
  },
  [CLEAR_GENERATE_USER_PAYLOAD](state) {
    state.generateUserPayload = { ...DEFAULT_GENERATE_USER_PAYLOAD }
  },
  [SAVE_RESELLER_SERVERS](state, data) {
    if (data && data.length > 0) {
      state.resellerServers = groupByCountry(data)
    }
  },
  [CLEAR_RESELLER_SERVERS](state) {
    state.resellerServers = {}
  },
  [SET_RESPONSE_STATE](state, { type, status }) {
    state.responses[type] = status
  },
}

export default {
  state,
  actions,
  mutations,
  getters,
}
